'use client';

import { Fragment, useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
const ReactPlayer = dynamic(() => import('react-player'), {
  ssr: false
});

import IconTriangle from 'app/components/sprites/IconTriangle';
import { IVideo } from 'app/interfaces/common';
import ImageBg from 'app/components/common/ImageBg';
import { sanitizeEmbed } from 'app/utility/textProcessing';

interface IVideoPlayerProps {
  video: IVideo;
  className?: string;
}

export default function VideoPlayer({ video, className }: IVideoPlayerProps) {
  const { type, embed, file, image } = video;
  const [isPlaying, setPlaying] = useState(false);

  const [embedContent, setEmbedContent] = useState<string | null>(null);

  useEffect(() => {
    if (embed) {
      setEmbedContent(sanitizeEmbed(embed));
    }
  }, [embed]);

  if (type === 'file' && !file?.url) return null;
  if (type === 'embed' && !embed) return null;

  return (
    <div
      className={classNames(
        'relative w-full pb-16/9 overflow-hidden rounded bg-graphite isolate',
        className
      )}
    >
      {type === 'file' && file?.url && (
        <Fragment>
          <ReactPlayer
            className="react-player"
            url={file.url}
            playing={isPlaying}
            controls={isPlaying}
            width="100%"
            height="100%"
          />

          {!!image && (
            <div
              className={classNames(
                'group absolute top-0 left-0 w-full h-full cursor-pointer transition-opacity duration-600',
                isPlaying ? 'pointer-events-none opacity-0' : 'opacity-100'
              )}
              onClick={() => setPlaying(true)}
            >
              <Fragment>
                <ImageBg
                  src={image.url}
                  alt={image.alt || ''}
                  fill
                  className="object-cover rounded"
                  skeletonTheme="graphite"
                  placeholderDataURL={image.placeholder_data_url}
                />

                <div
                  className={classNames(
                    'absolute bottom-1/2 right-1/2 translate-x-1/2 translate-y-1/2 flex justify-center items-center w-15 h-15',
                    'bg-green rounded hover:scale-105 transition-transform duration-400'
                  )}
                  aria-label="Запустить."
                >
                  <IconTriangle />
                </div>
              </Fragment>
            </div>
          )}
        </Fragment>
      )}

      {type === 'embed' && embedContent && (
        <div
          className="absolute top-0 left-0 w-full h-full [&_iframe]:w-full [&_iframe]:h-full"
          dangerouslySetInnerHTML={{ __html: embedContent as string }}
        />
      )}
    </div>
  );
}
